import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { isNil, noop, toString } from 'lodash/fp';
import { useTheme } from 'app/components/core/ThemeProvider';
import { useDefaultId, useEventCallback } from 'app/lib/hooks';
import { CharacterCount } from 'app/components/core/CharacterCount';
import { FieldLabel } from 'app/components/core/FieldLabel';
import { useErrorTooltip } from 'app/components/core/ErrorTooltip';
import * as styles from './Textarea.css';

const cx = classnames.bind(styles);

export const TextareaVariant = {
    OUTLINE: 'outline',
    FILL: 'fill'
};

const VARIANT_CLASSES = {
    [TextareaVariant.OUTLINE]: 'variant-outline',
    [TextareaVariant.FILL]: 'variant-fill'
};

export function Textarea({
    testId = null,
    className = null,
    id: suppliedId = null,
    name = null,
    value = '',
    disabled = false,
    label = null,
    onChange = noop,
    onBlur = noop,
    error = null,
    variant = TextareaVariant.OUTLINE,
    maxChars = null,
    children,
    ...props
}) {
    const { themeClassname } = useTheme();
    const id = useDefaultId('textarea', suppliedId);
    const handleChange = useEventCallback(onChange);
    const handleBlur = useEventCallback(onBlur);

    const classes = cx('base', themeClassname, VARIANT_CLASSES[variant], className);

    const hasError = Boolean(error?.length);
    const showMaxCharsSoftValidation = maxChars && !hasError;

    const { ErrorTooltip, getTooltipProps, getReferenceProps } = useErrorTooltip();

    return (
        <div className={classes} data-testid={testId}>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <textarea
                autoComplete="off"
                id={id}
                name={name}
                disabled={disabled}
                className={classnames(styles.textarea)}
                value={isNil(value) ? '' : toString(value)}
                onChange={handleChange}
                maxLength={maxChars}
                aria-invalid={hasError}
                {...getReferenceProps({
                    onBlur: handleBlur,
                    ...props
                })}
            />
            {children}
            {error && <ErrorTooltip error={error} {...getTooltipProps()} />}
            {showMaxCharsSoftValidation && (
                <CharacterCount limit={maxChars} value={value?.length} />
            )}
        </div>
    );
}

Textarea.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * Used by a wrapper Field component to link the value in Form state.
     */
    name: PropTypes.string,

    /**
     * A unique ID for the component. Used to link the contained <label> and <textarea>
     * for better accessibility.
     */
    id: PropTypes.string,

    /**
     * Prevent the user from interacting with the component. If this prop isn't
     * set, the component can still be disabled from a containing element, for
     * example a `<fieldset>`.
     */
    disabled: PropTypes.bool,

    /**
     * When supplied, creates a <label> element linked to the <textarea> with the
     * specified phrasing content.
     */
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

    /**
     * The value of the input. When used with <Field>, this will automatically be
     * set from the associated form state.
     */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /**
     * Function to call whenever the input changes. When used with <Field>, this will
     * automatically be configured to update the associated form state.
     */
    onChange: PropTypes.func,

    /**
     * Function to call whenever the input is blurred. When used with <Field>, this will
     * automatically be configured to update the associated form state.
     */
    onBlur: PropTypes.func,

    /**
     * When present, will highlight the input field in red and display the provided
     * error message or messages below it.
     */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

    /**
     * Component variant.
     */
    variant: PropTypes.oneOf(Object.values(TextareaVariant)),

    /**
     * Maximum allowed characters (used for soft validation FieldHint)
     */
    maxChars: PropTypes.number
};
